import L from 'leaflet';

// The customer's location, drawn on the Karte section of the customer page.
//
// The template decides whether there is anything to draw: when the customer has
// no coordinates it renders the German hint instead of the map container, so
// this module finds no element and returns. That is the whole fallback - there
// is no empty grey tile box to hide, because the box is never rendered.

// Mapbox's raster tile endpoint, drawn by Leaflet as a plain XYZ layer. Raster
// rather than mapbox-gl-js vector tiles: the GL library is 200 KB+ gzipped and
// would roughly double the bundle app-wide, since esbuild builds one file with
// no code splitting, and this page draws a single marker.
const TILE_URL =
  'https://api.mapbox.com/styles/v1/mapbox/streets-v12/tiles/256/{z}/{x}/{y}?access_token={accessToken}';

// Required by Mapbox's terms and by OpenStreetMap's licence - the underlying
// data is theirs. Kept in the corner of the map, which is where Leaflet puts it.
const ATTRIBUTION =
  '&copy; <a href="https://www.mapbox.com/about/maps/">Mapbox</a> ' +
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';

// Close enough to see the building and the streets around it, which is what
// somebody opening this page wants to know. The city-level view a lower zoom
// gives answers a question the address line already answered.
const ZOOM = 16;

// The candidate markers, and how they stay in step with a list that refetches.
//
// This is the awkward part and worth reading before changing it. The results
// region is swapped by shared/liveList.js, which replaces its innerHTML - and
// the map is deliberately NOT inside that region: Leaflet would be torn out of
// the DOM on every filter change and have to be rebuilt, which flickers, loses
// the zoom somebody set and costs a fresh round of tile requests.
//
// So the pins cannot live in the swapped markup. Instead the server puts the
// marker list on one data attribute inside the region (see
// views/kunden/_umkreisResults.pug), and this observes the region for a
// replacement and redraws the layer from whatever arrived. The rows and the
// pins therefore come from ONE response and cannot disagree - which is the
// whole reason this is not a second fetch of its own.
//
// Observed rather than hooked onto the live list's refresh(): liveList handles
// its own pager clicks and form changes internally, so a caller-side
// `await refresh()` would cover only the refreshes this module triggers and
// would quietly leave stale pins after a page click.
const UMKREIS_REGION = 'kandidaten-umkreis';

// Distinct from the customer's default pin on purpose: there is one of those
// and many of these, and the eye has to be able to find the address the page is
// about. A circle also stays legible at the small sizes a dozen overlapping
// markers end up at.
const KANDIDAT_MARKER = {
  radius: 7,
  weight: 2,
  color: '#1d4ed8',
  fillColor: '#3b82f6',
  fillOpacity: 0.65
};

// Escapes the four characters that matter inside a popup built as HTML.
//
// The names and job titles come from the database, so they are somebody's
// input: a candidate called `<img onerror=...>` would otherwise run script in
// the popup. Leaflet's bindPopup takes HTML, which is what makes this
// necessary - the link below is why plain text is not an option.
function escapeHtml(wert) {
  return String(wert == null ? '' : wert)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// One popup: who they are, what they do, how far away, and a link when the
// viewer could reach them.
//
// The link mirrors the table exactly - a row the viewer cannot reach renders as
// plain text there and must not become clickable here, or the map would be the
// way around a rule the list respects.
function popupHtml(eintrag) {
  const name = escapeHtml(eintrag.name);
  const kopf = eintrag.sichtbar
    ? `<a href="/kandidaten/${encodeURIComponent(eintrag.id)}">${name}</a>`
    : `<strong>${name}</strong>`;

  const zeilen = [eintrag.beruf, eintrag.km == null ? null : `${eintrag.km} km`]
    .filter(Boolean)
    .map((wert) => escapeHtml(wert))
    .join(' · ');

  return zeilen ? `${kopf}<br>${zeilen}` : kopf;
}

function initUmkreisLayer(karte) {
  const region = document.getElementById(UMKREIS_REGION);
  if (!region) return;

  // One layer group, emptied and refilled rather than re-created: it is added
  // to the map once, so a redraw never touches the map's own layer list.
  const ebene = L.layerGroup().addTo(karte);

  function zeichne() {
    ebene.clearLayers();

    const traeger = region.querySelector('[data-umkreis-marker]');
    if (!traeger) return;

    let marker = [];
    try {
      marker = JSON.parse(traeger.dataset.umkreisMarker) || [];
    } catch {
      // A malformed payload leaves the map with the customer's pin and no
      // candidates, which is the same picture somebody with nobody nearby
      // gets. Swallowed rather than thrown: the table beside it is correct and
      // readable, and taking main.js down over a pin is worse.
      return;
    }

    marker.forEach((eintrag) => {
      const breite = Number(eintrag.lat);
      const laenge = Number(eintrag.lng);
      if (!Number.isFinite(breite) || !Number.isFinite(laenge)) return;

      // [lat, lng] here: Leaflet takes latitude first, the opposite of the
      // GeoJSON order the server sends. Written out rather than spread, so the
      // swap cannot happen by accident - the same note the customer's own
      // marker carries above.
      L.circleMarker([breite, laenge], KANDIDAT_MARKER)
        // Bound rather than opened: there are many of these, so there is
        // something to choose between - unlike the single customer pin.
        .bindPopup(popupHtml(eintrag))
        .addTo(ebene);
    });
  }

  zeichne();

  // Every swap, whatever triggered it. childList on the region is enough:
  // liveList replaces innerHTML, so the carrier element is a direct child that
  // gets removed and re-added wholesale.
  new MutationObserver(zeichne).observe(region, { childList: true });
}

export function initKundeKarte() {
  const container = document.getElementById('kunde-karte');
  if (!container) return;

  const { lng, lat, token, name } = container.dataset;

  const laenge = Number(lng);
  const breite = Number(lat);

  // The template only renders this element with both coordinates set, so a
  // non-numeric pair here means something upstream is wrong rather than a
  // customer simply not being geocoded. Bail rather than ask Leaflet to centre
  // on NaN, which throws inside the library and takes the rest of main.js down.
  if (!Number.isFinite(laenge) || !Number.isFinite(breite)) return;

  // Without a public token there is nothing to authenticate the tile requests
  // with. The template shows its own hint in that case; this is the guard for
  // the element existing anyway.
  if (!token) return;

  // Leaflet resolves its default marker images relative to the script that
  // loaded it, which is /js/bundle.js - so it would look for /js/images/. The
  // images are vendored beside the stylesheet instead, by scripts/copyIcons.js.
  L.Icon.Default.imagePath = '/vendor/leaflet/images/';

  // [lat, lng] here: Leaflet takes latitude first, the opposite of the GeoJSON
  // order the coordinates are stored in. The data attributes are named so the
  // swap has to be written out rather than happening by accident.
  const karte = L.map(container, {
    center: [breite, laenge],
    zoom: ZOOM,
    // The page scrolls; a wheel over an embedded map that swallows the scroll
    // is a well-known annoyance. Dragging and the +/- control still work.
    scrollWheelZoom: false
  });

  L.tileLayer(TILE_URL, {
    accessToken: token,
    attribution: ATTRIBUTION,
    maxZoom: 19
  }).addTo(karte);

  const marker = L.marker([breite, laenge]).addTo(karte);

  // The customer's name on the pin, so a screenshot of the map still says which
  // customer it is. Opened rather than left to a click: there is exactly one
  // marker, so there is nothing to choose between.
  if (name) {
    marker.bindPopup(name).openPopup();
  }

  // The candidates in the radius, layered over the customer pin. Behind its
  // own element guard rather than a second init<Thing>(): the layer lives on
  // *this* map instance, and a separate module would have to be handed it -
  // a shared mutable across modules, or a second map on the same div.
  initUmkreisLayer(karte);

  // The section is inside a card that the browser lays out after this runs in
  // some paths, which leaves Leaflet believing the container is 0px tall and
  // drawing a single tile in the corner. Recalculating once on the next frame
  // is the standard fix.
  requestAnimationFrame(() => karte.invalidateSize());
}
